import { useState } from 'react';
import type { FileRejection } from 'react-dropzone';
import {
  Button,
  Field,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Tabs,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import { CATEGORIES, type Category } from '@inference.engine/guidelines/web';
import { formatCategoryLabel } from '@/features/guidelines/constants';
import {
  useCreateGuideline,
  useGenerateGuidelinesFromFiles,
  useImportGuidelinesFromFiles,
  useUpdateGuideline,
} from '@/features/guidelines/hooks';
import { GuidelineImportDropzone } from './GuidelineImportDropzone';
import { GuidelineImportFormatHint } from './GuidelineImportFormatHint';
import {
  MAX_DESCRIPTION_LENGTH,
  MAX_NAME_LENGTH,
  TEXT_FILE_FORMATS,
} from './GuidelineFormSheet.types';
import type { GuidelineFormSheetProps } from './GuidelineFormSheet.types';

const CATEGORY_NAMES = Object.keys(CATEGORIES) as Category[];

type CreateTab = 'manual' | 'import';

/**
 * Right-side counterpart to the legacy centre `CreateGuidelineModal` /
 * `EditGuidelineModal`, carrying every capability of both: the manual form,
 * the document-import path (text files to the import route, everything else
 * to AI generation, mixed types are rejected), and edit's
 * change-gated save. Mounted once by the pane and remounted via a `key`
 * derived from mode + guideline id at the call site — the same mechanism
 * `FolderFormSheet` uses, since `useState` initialisers only read `guideline`
 * once, at mount.
 */
export function GuidelineFormSheet({
  open,
  mode,
  guideline,
  folderId,
  onClose,
}: GuidelineFormSheetProps) {
  const { t } = useTranslation('knowledge');
  const isEdit = mode === 'edit';

  const [activeTab, setActiveTab] = useState<CreateTab>('manual');
  const [name, setName] = useState(guideline?.name ?? '');
  const [description, setDescription] = useState(guideline?.description ?? '');
  const [category, setCategory] = useState<Category | ''>(guideline?.category ?? '');
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);

  const { mutate: createGuideline, isPending: isCreating } = useCreateGuideline();
  const { mutate: updateGuideline, isPending: isUpdating } = useUpdateGuideline();
  const { mutate: generateFromFiles, isPending: isGenerating } = useGenerateGuidelinesFromFiles();
  const {
    mutate: importFromFiles,
    isPending: isImporting,
    error: importError,
    reset: resetImport,
  } = useImportGuidelinesFromFiles();

  const isSubmitting = isCreating || isUpdating || isGenerating || isImporting;

  const isTextFile = (file: File) => file.type in TEXT_FILE_FORMATS;
  const allTextFiles = files.length > 0 && files.every(isTextFile);
  const allNonTextFiles = files.length > 0 && files.every((file) => !isTextFile(file));

  const isManualFormValid =
    name.trim().length > 0 && description.trim().length > 0 && category !== '';
  const isImportFormValid = files.length > 0;
  const isCreateFormValid = activeTab === 'manual' ? isManualFormValid : isImportFormValid;

  const hasChanges =
    isEdit && guideline
      ? name.trim() !== guideline.name ||
        description.trim() !== guideline.description ||
        category !== guideline.category
      : false;

  const isSaveDisabled = isEdit
    ? !isManualFormValid || !hasChanges || isSubmitting
    : !isCreateFormValid || isSubmitting;

  const sheetTitle = isEdit
    ? t('guidelines.editGuidelineModal.editGuideline')
    : t('guidelines.createGuidelineModal.addGuideline');

  const handleDrop = (acceptedFiles: File[]) => {
    setFileError(null);
    resetImport();
    setFiles((previous) => [...previous, ...acceptedFiles]);
  };

  const handleDropRejected = (fileRejections: FileRejection[]) => {
    const fileNames = fileRejections.map((rejection) => rejection.file.name).join(', ');
    setFileError(t('guidelines.createGuidelineModal.unsupportedFileType', { fileNames }));
  };

  const handleRemoveFile = (index: number) => {
    resetImport();
    setFiles((previous) => previous.filter((_, fileIndex) => fileIndex !== index));
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  const handleSubmit = () => {
    if (isSubmitting) return;

    if (isEdit) {
      if (!guideline || !isManualFormValid || !hasChanges) return;
      updateGuideline(
        {
          guidelineId: guideline.id,
          name: name.trim(),
          description: description.trim(),
          category: category,
          folderId,
        },
        { onSuccess: onClose },
      );
      return;
    }

    if (!isCreateFormValid) return;

    if (activeTab === 'manual') {
      createGuideline(
        {
          name: name.trim(),
          description: description.trim(),
          category: category as Category,
          folderId,
        },
        { onSuccess: onClose },
      );
    } else if (!allTextFiles && !allNonTextFiles) {
      setFileError(t('guidelines.createGuidelineModal.mixedFileTypes'));
    } else if (allTextFiles) {
      importFromFiles({ files, folderId }, { onSuccess: onClose });
    } else {
      generateFromFiles({ files, folderId }, { onSuccess: onClose });
    }
  };

  const submitLabel = isSubmitting
    ? t('guidelines.createGuidelineModal.saving')
    : isEdit
      ? t('guidelines.editGuidelineModal.saveChanges')
      : activeTab === 'import' && allTextFiles
        ? t('guidelines.createGuidelineModal.import')
        : activeTab === 'import' && allNonTextFiles
          ? t('guidelines.createGuidelineModal.generateWithAi')
          : t('guidelines.createGuidelineModal.saveChanges');

  return (
    <Sheet open={open} onOpenChange={(next) => !next && handleClose()}>
      <SheetContent className="w-[560px] rounded-l-lg p-xl sm:max-w-none">
        <SheetHeader className="p-0">
          <SheetTitle>{sheetTitle}</SheetTitle>
        </SheetHeader>
        <div className="-m-1.5 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-1.5">
          {!isEdit && (
            <Tabs value={activeTab} onValueChange={(next) => setActiveTab(next as CreateTab)}>
              <TabsList>
                {/* eslint-disable i18next/no-literal-string -- tab identifiers, not copy */}
                <TabsTrigger value="manual" disabled={isSubmitting}>
                  {t('guidelines.createGuidelineModal.createManually')}
                </TabsTrigger>
                <TabsTrigger value="import" disabled={isSubmitting}>
                  {t('guidelines.createGuidelineModal.importFromDocument')}
                </TabsTrigger>
                {/* eslint-enable i18next/no-literal-string */}
              </TabsList>
            </Tabs>
          )}

          {(isEdit || activeTab === 'manual') && (
            <>
              <Field>
                <FieldLabel htmlFor="guideline-form-name">
                  {t('guidelines.createGuidelineModal.name')}
                </FieldLabel>
                <Input
                  id="guideline-form-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  disabled={isSubmitting}
                  maxLength={MAX_NAME_LENGTH}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="guideline-form-description">
                  {t('guidelines.createGuidelineModal.description')}
                </FieldLabel>
                <Textarea
                  id="guideline-form-description"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  disabled={isSubmitting}
                  maxLength={MAX_DESCRIPTION_LENGTH}
                  rows={4}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="guideline-form-category">
                  {t('guidelines.createGuidelineModal.category')}
                </FieldLabel>
                <Select
                  value={category}
                  onValueChange={(next) => setCategory(next as Category)}
                  disabled={isSubmitting}
                >
                  <SelectTrigger id="guideline-form-category">
                    <SelectValue
                      placeholder={t('guidelines.createGuidelineModal.selectCategory')}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_NAMES.map((categoryName) => (
                      <SelectItem key={categoryName} value={categoryName}>
                        {formatCategoryLabel(categoryName)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </>
          )}

          {!isEdit && activeTab === 'import' && (
            <GuidelineImportDropzone
              files={files}
              onDrop={handleDrop}
              onDropRejected={handleDropRejected}
              onRemoveFile={handleRemoveFile}
              error={fileError}
              disabled={isSubmitting}
            />
          )}

          {!isEdit && activeTab === 'import' && (
            <GuidelineImportFormatHint error={importError?.message ?? null} />
          )}
        </div>
        <SheetFooter className="flex-row items-center justify-end gap-2 p-0">
          <Button variant="outline" size="lg" onClick={handleClose} disabled={isSubmitting}>
            {t('guidelines.createGuidelineModal.cancel')}
          </Button>
          <Button variant="default" size="lg" onClick={handleSubmit} disabled={isSaveDisabled}>
            {submitLabel}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

GuidelineFormSheet.displayName = 'GuidelineFormSheet';
